import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Modal from '@oberoninternal/travelbase-ds/components/layout/Modal';
import ModalHeader from '@oberoninternal/travelbase-ds/components/layout/ModalHeader';
import { Sesame } from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { useFormikContext } from 'formik';
import isEqual from 'lodash.isequal';
import pick from '../utils/pick';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import Stack from './Stack';
import useFullscreenToggleEvent from '../hooks/useFullscreenToggleEvent';

export interface Props extends Pick<Sesame, 'open' | 'onClose'> {
    filtersActive: boolean;
    removeActiveFilters: () => void;
    /**
     * Keys to check whether the filters are applied or not. Looks at initial values and compares them to the current values
     */
    dirtyCheckKeys: string[];
}

const SearchFiltersModal: FC<React.PropsWithChildren<Props>> = ({
    onClose,
    open,
    filtersActive,
    removeActiveFilters,
    dirtyCheckKeys: filterKeys,
    children,
}) => {
    const { submitForm, values, initialValues } = useFormikContext();
    const formFiltersActive = useMemo(
        () => !isEqual(pick(initialValues, filterKeys), pick(values, filterKeys)),
        [filterKeys, initialValues, values]
    );
    useFullscreenToggleEvent(open);

    return (
        <StyledModal onClose={onClose} open={open}>
            <ModalHeader style={{ textTransform: 'capitalize' }}>
                <FormattedMessage defaultMessage="filters" />
            </ModalHeader>
            <ScrollBody>
                <Stack mt={4} spacing={4}>
                    {children}
                </Stack>
            </ScrollBody>
            <Actions>
                <Button
                    style={{ width: '100%' }}
                    onClick={() => {
                        onClose();
                        // As the modal is rendered through a portal, the form won't be automatically submitted
                        // so we need to do it manually.
                        submitForm();
                    }}
                >
                    <FormattedMessage defaultMessage="Filters toepassen" />
                </Button>
                <Button
                    variant="outline"
                    disabled={!formFiltersActive && !filtersActive}
                    onClick={() => {
                        removeActiveFilters();
                    }}
                    style={{ width: '100%' }}
                    type="button"
                >
                    <FormattedMessage defaultMessage="Filters verwijderen" />
                </Button>
            </Actions>
        </StyledModal>
    );
};

export default SearchFiltersModal;

const StyledModal = styled(Modal)`
    /* Make the dialog a vertical flex container so the filter list gets its own
       scroll region (ScrollBody) and the action buttons stay pinned as a footer.
       This replaces a position: sticky footer, which is unreliable inside the
       embed iframe where the scroll container's height is driven by the host
       (100dvh resolves to the iframe's height, not the visible viewport). */
    && {
        display: flex;
        flex-direction: column;
    }
`;

const ScrollBody = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
`;

const Actions = styled.div`
    flex-shrink: 0;
    width: 100%;
    padding-top: ${({ theme }) => theme.spacing['40_Standard']};
    border-top: 1px solid ${({ theme }) => theme.colors.neutral['20']};
    background: ${({ theme }) => theme.colors.neutral['0']};
    button + button {
        margin-top: ${({ theme }) => theme.spacing['30_Small']};
    }
`;
